import { useEffect, useRef, useMemo, useState } from "react"
import { pick, omitBy, isNil, isEqual, debounce, pickBy } from "lodash"
import { useSaveContextPreferences, useReadContextPreference } from "hooks/preferences"
import { OK, ERROR } from "helpers/data-statuses"

/**
 * Single hook that handles both loading and saving preferences
 * Replaces the need for separate PreferenceSaver and PreferenceLoader components
 *
 * @param {string} subType - The preference subtype (e.g., "dashboard", "asset-view")
 * @param {string[]} preferenceFields - Array of field names to save as preferences
 * @param {object} state - Current state to save preferences from
 * @param {function} dispatch - Dispatch function to update state with loaded preferences
 * @param {function} setPreferencesAction - Action creator for setting preferences (e.g., setPreferences)
 * @param {object} [options] - Optional configuration
 * @param {object} [options.setPreferenceValidationOptions] - Function that receives preferences to validate
 *  and a callback to set the validation result. The callback will receive the validation result with the preferences
 *  and isCorrected boolean indicating if the preferences were corrected.
 * @param {number} [options.debounceTime=300] - Debounce time for saving (default 300ms)
 * @returns {boolean} isPreferencesLoaded - Whether preferences have been loaded
 */
export function usePreferences(
  subType,
  preferenceFields,
  state,
  dispatch,
  setPreferencesAction,
  { setPreferenceValidationOptions, debounceTime = 300 } = {},
) {
  const savePreferences = useSaveContextPreferences(subType)
  const [preferences, preferenceStatus] = useReadContextPreference(subType)
  const [validationResult, setValidationResult] = useState(null)
  const [isPreferencesLoaded, setIsPreferencesLoaded] = useState(false)

  const hasPreferenceRequestFinished = preferenceStatus === OK || preferenceStatus === ERROR
  const isPreferencesValid = !setPreferenceValidationOptions || !!validationResult

  // ===== LOADING LOGIC (runs once on mount) =====
  const isInitialLoadRef = useRef(false)
  const lastValidationResultRef = useRef(null)

  // ===== SAVING LOGIC (runs on state changes) =====
  const previousPreferencesRef = useRef(null)
  const isInitialSaveRef = useRef(true)

  // Create a debounced save function to avoid rapid successive saves
  const debouncedSave = useMemo(
    () => debounce((preferencesToSave) => {
      savePreferences(preferencesToSave)
    }, debounceTime),
    [savePreferences, debounceTime],
  )

  useEffect(() => {
    if (isInitialLoadRef.current) {
      return
    }

    if (hasPreferenceRequestFinished) {
      isInitialLoadRef.current = true

      const cleanedPreferences = preferences ? pickBy(preferences, (value) => !isNil(value)) : {}
      if (setPreferenceValidationOptions) {
        setPreferenceValidationOptions({ preferences: cleanedPreferences, callback: setValidationResult })
      } else {
        if (Object.keys(cleanedPreferences).length > 0) {
          dispatch(setPreferencesAction(cleanedPreferences))
        }
        setIsPreferencesLoaded(true)
      }
    }
  }, [hasPreferenceRequestFinished, preferences, setPreferenceValidationOptions, setValidationResult, setPreferencesAction, dispatch])

  useEffect(() => {
    if (validationResult !== null && validationResult !== lastValidationResultRef.current) {
      lastValidationResultRef.current = validationResult
      if (validationResult.isCorrected) {
        debouncedSave(validationResult.preferences)
      }
      dispatch(setPreferencesAction(validationResult.preferences))
      setIsPreferencesLoaded(true)
    }
  }, [validationResult, debouncedSave, setPreferencesAction, dispatch])

  useEffect(() => {
    if (!isPreferencesLoaded || !state) {
      return
    }

    const currentPreferences = omitBy(pick(state, preferenceFields), isNil)

    if (isInitialSaveRef.current) {
      previousPreferencesRef.current = currentPreferences
      isInitialSaveRef.current = false
      return
    }

    if (!isEqual(previousPreferencesRef.current, currentPreferences)) {
      previousPreferencesRef.current = currentPreferences
      if (Object.keys(currentPreferences).length > 0) {
        debouncedSave(currentPreferences)
      }
    }
  }, [state, isPreferencesLoaded, debouncedSave, preferenceFields])

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  return isPreferencesLoaded && isPreferencesValid
}
